import { CSSProperties, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Check, CheckItem, CheckSplitItemRequestDef, CheckSplitItemsRequestDef } from 'Src/feature/check/models';
import { useCheck } from 'Src/feature/check/useCheck';
import { useCurrentCheck } from 'Src/feature/check/useCurrentCheck';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { usePrinter } from 'Src/feature/printer/usePrinter';
import { useOnErrorShowToast } from 'Src/feature/toasts/useOnErrorShowToast';
import useRoute from 'Src/routes/useRoute';
import { getRemCssValue } from 'Src/utils/css';
import { uniqueInt } from 'Src/utils/number';
import { sortByLabel } from 'Src/utils/sort';

const sectionId = 'useCheckSplit';

// 2024/05 BK
// This is going to be long comment... I apologize.
// This hook and the corresponding components are a bit messed up. We were attempting to keep a sort of ledger of changes
// on the client side of item splits, qty changes, and moves, up until the user clicks the done button where we want to
// commit everything.
// A lot of the problems we are running into stem from the fact that the server needs to know what we want to do, and possibly
// the order of operations or something. This is hard to express in a single API endpoint.
// Therefore, in some future state, it has been proposed that we create some sort of temp copy of the check while we do things
// like this. Then we can take atomic actions against that temp check. So for example, instead of changing the qty and spliting
// an item, this would be two separate calls to the API, first to change the qty, and then to split. This way the server can
// do all the accounting of things, and do any price calculations and other hard things. The UI then only has to render the
// resulting check and each action is a simple call to the API. Finally, when done is clicked, the old order is overwritten
// with the new check.
// This is "simple" conceptually, but a bit more difficult to actually implement. At this time we simply don't have the time
// to do this. So this page had some fixes put in place to clean it up as best as we can, and the better fix is food for
// thought later.

/**
 * @property {string} uid
 * - a unique id to assist w/ unique react keys
 * - used to track split items
 * - branching logic (when a new CheckItemSplitUI is constructed):
 *    - when !options.maintainItemUid
 *        - generate an uid each construction
 *    - when options.maintainItemUid
 *        - used during a split to remove the previous item
 * @function select
 * - toggle the check item into a selected/de-selected state
 */
class CheckItemSplitUI extends CheckItem {
  isSelected = false;
  // Client-side representation of the split
  split = {
    type: undefined as unknown as CheckSplitItemRequestDef['type'],
    by: 0,
    quantity: 0,
  };
  readonly _uid: string = '';

  get uid() { return `${this.id }-${this._uid}`;}

  get is() {
    return {
      removed: this.status === 'Removed',
      voided: this.status === 'Voided',
      moved: this.status === 'Moved',
      selected: this.isSelected,
      split: !!this.splitBy || !!this.split.by,
      quantityChanged: this.quantity !== this.split.quantity && this.split.quantity !== 0,
    };
  }

  get can() {
    return {
      edit: !this.is.voided && !this.is.removed && !this.is.moved,
      split: !this.splitBy && !this.split.by,
    };
  }

  // client side augmentation of check item values
  // - the check properties/values gets calculates server side
  // - when we change the check client side we have to do our own "placeholder" calculations
  get values() {
    const a = {
      num: this.quantity || 1,
      den: this.splitBy || 1,
    };
    const b = {
      num: this.split.quantity || 1,
      den: this.split.by || 1,
    };
    if ((a.num !== b.num && a.den !== 1 && b.den !== 1) || a.den !== b.den) {
      let num = 1;
      let den = 1;
      // only numerator change
      if (a.num !== b.num && a.den === b.den) {
        // TODO: this will only happen if we pull in "quantity" changes
        // - taking a guess until I better understand how split/quantity factor
        num = b.num;
        den = a.den;
      }
      // only denominator change
      else if (a.num === b.num && a.den !== b.den) {
        num = a.num;
        den = a.den * b.den;
      }
      // both numerator & denominator change
      else if (a.num !== b.num && a.den !== b.den) {
        // TODO: this will only happen if we pull in "quantity" changes
        // - taking a guess until I better understand how split/quantity factor
        num = Math.abs(a.num - b.num);
        den = a.den * b.den;
      }
      const fraction = num/den;
      const gross = Math.round(((this.gross * fraction) + Number.EPSILON) * 100) / 100;
      return { gross, splitBy: den, quantity: num };
    }
    const { gross, splitBy, quantity } = this;
    return { gross, splitBy, quantity };
  }

  // check item has read only values (need to augment while client side split happens)
  /** @deprecated - move this to vanilla react state */
  update = (part?: Partial<CheckItemSplitUI>) => {
    if (part) Object.assign(this, part);
    return this;
  };

  constructor(checkItem: CheckItem | CheckItemSplitUI, options?: { maintainItemUid?: boolean }) {
    super(checkItem);
    if (options?.maintainItemUid) {
      this._uid = (checkItem as CheckItemSplitUI)._uid;
    } else {
      this._uid = `CheckItemSplitUI-${uniqueInt()}`;
    }
  }
}

/**
 * @function select
 * - toggle the Check into a selected/de-selected state
 * - when selecting a check we are marking that check to intake split/move items
 * @function selectItems
 * - toggle all the CheckItem(s) on this Check into a selected/de-selected state
 * - when selecting a check items we are marking that check items to be moved/split
 */
class CheckSplitUI extends Check {
  isSelected = false;
  items: CheckItemSplitUI[] = [];
  seatName: string | undefined = undefined;

  get itemSelections() {
    const selected = [] as  CheckItemSplitUI[];
    const unselected = [] as  CheckItemSplitUI[];
    this.items.forEach((item) => {
      if (item.is.selected) selected.push(item);
      else unselected.push(item);
      return item;
    });
    return { selected, unselected };
  }

  // options
  // - options.maintainItemUid - necessary when splitting
  constructor(check: Partial<Check>, options?: { maintainItemUid?: boolean }) {
    super(check);
    this.items = (check.items || []).map((item) => new CheckItemSplitUI(item as CheckItemSplitUI, options));
  }
}

interface UseCheckSplitOptions {
  currentCheckSelectedCheckItemIds: CheckItem['id'][];
}

const useCheckSplit = (options: UseCheckSplitOptions) => {
  const { t } = useTranslation();
  const { crumbs, navigate, route } = useRoute();
  const log = useLogger().getSubLogger({ name: sectionId });
  const allocateNextCheckNumber = useCheck.mutate.allocateNextNumber();
  const splitCheck = useCheck.mutate.splitCheck(useOnErrorShowToast({ title: t('checkSplit.label') }));
  const currentCheck = useCurrentCheck();
  const printerSend = usePrinter.mutate.send();

  const [checks, checksSet] = useState([] as CheckSplitUI[]);
  const [hasChanges, hasChangesSet] = useState(false);

  // has some selections in any of the stores
  const hasSomeCheckItemSelections = (_checks: CheckSplitUI[]) => _checks.some((chk) => chk.itemSelections.selected.length);
  const hasSomeAlreadySplitCheckItemSelections = (_checks: CheckSplitUI[]) => _checks.some((chk) => chk.itemSelections.selected.some((item) => {
    const numerator = item.split.quantity || item.quantity;
    const denominator = item.splitBy || item.split.by || 1;
    return numerator % denominator !== 0;
  }));

  const createEmptyCheck = async (baseCheck: CheckSplitUI, seatName?: string) => {
    const response = await allocateNextCheckNumber.mutateAsync();
    // start w/ first check number
    const label = `${baseCheck.number} - ${response.checkNumber}`;
    // using a negative id for client side created Check(s)
    //  - differentiate between new/existing checks
    //  - id is used in downstream ops ( ie - ui tracking )
    // when we send these new checks to the backend, we have to specify the newCheckNumber value
    // so that the backend uses the number we allocated above (augmented with source check number)
    const newCheck = new CheckSplitUI(new Check({ id: -store.checks.length, label, number: response.checkNumber, tableName: baseCheck.tableName }));
    if (seatName) newCheck.seatName = seatName;
    return newCheck;
  };

  const store = {
    get checks() {
      return checks;
    },
    get has() {
      return { changes: hasChanges, selections: hasSomeCheckItemSelections(store.checks) } as const;
    },
    hasSomeAlreadySplitCheckItemSelections,
    selected: {
      get checkIds() {
        return store.checks.filter((chk) => chk.isSelected).map(({ id }) => id);
      },
    },
    select: {
      check: (chk: CheckSplitUI) => {
        checksSet(checks.map((_chk) => {
          if (_chk.id === chk.id) {
            _chk.isSelected = !_chk.isSelected;
          }
          return _chk;
        }));
        hasChangesSet(true);
      },
      checkItem: (checkItem: CheckItemSplitUI) => {
        checksSet(checks.map((_chk) => {
          _chk.items = _chk.items.map((item) => {
            if (item.uid === checkItem.uid) {
              item.isSelected = !item.isSelected;
            }
            return item;
          });
          return _chk;
        }));
        hasChangesSet(true);
      },
    },
    deselect: {
      all: (_checks: CheckSplitUI[]) => {
        // deselect all checks & all check items
        const nextChecks = _checks.map((_chk) => {
          _chk.isSelected = false;
          _chk.items = _chk.items.map((item) => {
            item.isSelected = false;
            return item;
          });
          return _chk;
        });
        checksSet(nextChecks);
        return nextChecks;
      },
    },
    splitOrMove: {
      is: {
        get active() {
          return Boolean(store.selected.checkIds.length);
        },
      },
      get type() {
        if (!store.splitOrMove.is.active) return undefined;
        // multiple selections
        if (store.selected.checkIds.length > 1) return 'split-and-move';
        // single selection but it is the current check
        if (store.selected.checkIds.length === 1 && store.selected.checkIds[0] === currentCheck.query.data?.id) return 'split';
        return 'move';
      },
      // !!! new construction of CheckSplitUI requires options.maintainItemUid = true
      onButtonClick: () => {
        const from: CheckSplitUI[] = [];
        const to: CheckSplitUI[] = [];
        store.checks.forEach((nextChk) => {
          if (nextChk.itemSelections.selected.length) {
            from.push(new CheckSplitUI({ ...nextChk, items: nextChk.itemSelections.selected }, { maintainItemUid: true }));
          }
          if (store.selected.checkIds.includes(nextChk.id)) {
            to.push(new CheckSplitUI(nextChk, { maintainItemUid: true }));
          }
        });
        let nextChecks = store.moveOrSplit({ from, to });
        nextChecks = store.deselect.all(nextChecks);
        hasChangesSet(true);
        // add ghost check
        const emptyCheckCount = nextChecks.reduce((acc, cur) => cur.items.length === 0 || !cur.items ? ++acc : acc, 0);
        if (emptyCheckCount === 0) void store.onAddNewCheck();
      },
    },
    can: {
      get save() {
        return Boolean(store.checkSplits.length);
      },
      get cancel() {
        return store.splitOrMove.is.active || store.has.changes || store.can.save;
      },
      moveHere: (chk: CheckSplitUI) => {
        if (store.splitOrMove.is.active) return false;
        if (hasSomeCheckItemSelections(checks)) {
          // true when has none selected for the current check
          return !chk?.itemSelections.selected.length;
        }
        return false;
      },
       
      splitHere: (chk: CheckSplitUI) => {
        // is current check
        if (chk.id === currentCheck.query.data?.id) {
          // current check can split onto itself when any selections
          return hasSomeCheckItemSelections(checks);
        }
        // non-current check(s)
        // - need 2 checks & some check item selections
        return store.checks.length > 1
          ? hasSomeCheckItemSelections(checks)
          : false;
      },
      get splitBySeat() {
        return !hasSomeCheckItemSelections(checks) && !store.can.save;
      },
    },
    moveOrSplit: (options: { from: CheckSplitUI[], to: CheckSplitUI[] }) => {
      const { from, to } = options;

      let splitBy = to.length;
      // if we are splitting check items and keeping on the current check
      if (from.length === 1 && to.length === 1 && from[0].id === to[0].id) {
        // force to split handle check item splitting on the same check
        splitBy = 2;
        from.push(from[0]);
      }

      // checks should exist before moveOrSplit so no need to augment
      return store.checks.map((nextChk) => {
        let nextItems: CheckItemSplitUI[] = [...nextChk.items];
        const fromChk = from.find(({ id }) => nextChk.id === id);
        const toChk = to.find(({ id }) => nextChk.id === id);

        // current check is a "from" check
        if (fromChk) {
          const hasQuantityChanges = nextItems.some((item) => item.is.quantityChanged);
          // remove any items when a from check
          nextItems = nextItems.filter(({ uid }) => !fromChk.items.find((fromItem) => fromItem.uid === uid));
          // add back any quantity change items
          if (hasQuantityChanges) {
            const splitItems = from.reduce((accum, fromCheck) => {
              accum.push(...fromCheck.itemSelections.selected.map((fromItem) => new CheckItemSplitUI(fromItem).update({
                split: {
                  type: 'Quantity',
                  by: 0,
                  quantity: fromItem.quantity - fromItem.split.quantity,
                },
              })));
              return accum;
            }, [] as CheckItemSplitUI[]);
            nextItems.push(...splitItems);
          }
        }

        // current check is a "to" check
        if (toChk) {
          // is a split
          if (splitBy > 1) {
            const splitItems = from.reduce((accum, fromCheck) => {
              accum.push(...fromCheck.items.map((fromItem) => new CheckItemSplitUI(fromItem).update({
                split: {
                  // when splitting => 'Portion'
                  type: 'Portion',
                  by: splitBy,
                  quantity: fromItem.quantity,
                },
              })));
              return accum;
            }, [] as CheckItemSplitUI[]);
            nextItems.push(...splitItems);
          }
          // is a move
          else {
            from.forEach((fromCheck) => nextItems.push(...fromCheck.items.map((item) => new CheckItemSplitUI(item).update({
              split: {
                // when moving => 'Whole' || 'Quantity'
                type: item.split.type === 'Portion' ? 'Portion' : item.is.quantityChanged ? 'Quantity' : 'Whole',
                by: item.split.type === 'Portion' ? item.values.splitBy : 0,
                quantity: item.split.type === 'Portion' ? item.quantity : item.is.quantityChanged ? item.split.quantity : 0,
              },
            }))));
          }
        }
        nextChk.items = nextItems;
        return nextChk;
      });
    },
    // !!! new construction of CheckSplitUI requires options.maintainItemUid = true
    onMoveHereClick: (chk: CheckSplitUI) => {
      const from: CheckSplitUI[] = [];
      store.checks.forEach((nextChk) => {
        if (nextChk.itemSelections.selected.length) {
          const newFromChk = new CheckSplitUI(nextChk, { maintainItemUid: true });
          newFromChk.items = nextChk.itemSelections.selected;
          from.push(newFromChk);
        }
      });
      let nextChecks = store.moveOrSplit({ from, to: [new CheckSplitUI(chk, { maintainItemUid: true })] });
      nextChecks = store.deselect.all(nextChecks);
      hasChangesSet(true);
      // add ghost check
      const emptyCheckCount = nextChecks.reduce((acc, cur) => cur.items.length === 0 || !cur.items ? ++acc : acc, 0);
      if (emptyCheckCount === 0) void store.onAddNewCheck();
    },
    onAddNewCheck: async (seatName?: string) => {
      if (!store.checks.length) {
        // should never get here
        log.error('onAddNewCheck()', 'no starting check');
      }
      const newChk = await createEmptyCheck(store.checks[0], seatName);
      checksSet([...store.checks, newChk]);
    },
    // TODO: need to handle 'Quantity'
    // - when check item needs to be merged back together
    get checkSplits(){
      const checkSplits: CheckSplitItemsRequestDef[] = [];
      store.checks.forEach((chk) => {
        if (chk.items && chk.items.length) {
          if (chk.id === currentCheck.query.data?.id) {

            const originCheckItems = chk.items.filter((item) => item.split.type === 'Portion' || item.split.type === 'Quantity');
            if (originCheckItems.length) {
              checkSplits.push({
                originCheck: true,
                newCheck: false,
                items:  originCheckItems.map((item) => ({
                  itemId: item.id,
                  type: item.split.type,
                  seatNames: item?.seatNames,
                  quantity: item.split.type === 'Quantity' ? item.split.quantity : undefined,
                }))
              });
            }
          } else {
            checkSplits.push({
              checkId: undefined,
              originCheck: false,
              newCheck: true,
              newCheckNumber: chk.number,
              items:  chk.items.map((item) => ({
                itemId: item.id,
                type: item.split.type,
                seatNames: item?.seatNames,
                quantity: item.split.type === 'Quantity' ? item.split.quantity : undefined,
              }))
            });
          }
        }
      });
      return checkSplits;
    },
    onDone: async (print = false) => {
      const payload = {
        checkId: currentCheck.query.data?.id as number,
        split: { checkSplits: store.checkSplits },
      };
      log.debug('onSplit()', { payload });
      const response = await splitCheck.mutateAsync(payload);
      if (!splitCheck.error) {
        if (print) {
          // 2024/11 BK
          // With our new and improved pattern around mutations, it is ideal to call a '.mutate' and pass call back arguments
          // for onSuccess, onError, and onSettled as needed.
          // There is an issue with this though when you call multiple mutations on the same mutation object back-to-back.
          // In that case, the onSuccess, onError, and onSettled arguments are only called for the last mutate call.
          // So here we are looping over each resulting check of a split. We need to print each one in turn. But if we
          // made a loop and called mutate for each one, and let's say the 3rd out of 5 requests failed, then we
          // would never see that error.
          // One suggestion to handle this is that if you have logic that needs to run for each mutation, you "promote" that
          // up to the hook where the useMutate is declared. If you put a onError there, it will run for each mutation.
          // See this https://tkdodo.eu/blog/mastering-mutations-in-react-query under the section "Some callbacks might not fire"
          // and this https://tanstack.com/query/latest/docs/framework/react/guides/mutations#consecutive-mutations.
          // The above suggestion doesn't really work for us here though as what we want to do with these errors is specific
          // to this component and use case... we don't want all uses of usePrinter.mutate.send to have to deal with things
          // that are only relavent here. So we don't want to promote this error handling up to the hook.
          // So the solution is to use mutateAsync. That way we will can get a promise for each of the mutate calls.
          // However, we don't want to await each one in turn... what if the user split to like 100 checks? We would be stuck
          // on this screen waiting for 100 calls to the backend.
          // So instead we make an array of these promises that mutateAsync returns, and then we setup a 'then' callback.
          // TODO: For now we are just logging the results. Note that the failure we are capturing here is not a "print failure",
          // but rather a failure to ask the backend to print. "Print failure" result in their own toast already through the 
          // notification system. In the future we may want to show a toast. But what to do when print job request fails to ask
          // the backend to print has not been determined.
          const printMutations = response.map((chk) => printerSend.mutateAsync({ type: 'Guest', checkId: chk.id }));
          Promise.allSettled(printMutations).then((printResponses) => printResponses.forEach((pr) => {
            if (pr.status === 'rejected') log.error('printer send failed', { reason: pr.reason });
          }));
        }
        log.debug('onSplit()', { response });
        store.onBack();
      }
    },
    seats: {
      get selectedSeatNames() {
        // using a map as a collector to remove duplicates
        // - merging any previously set seat names into a single collection
        const seatNames = new Map();
        store.checks.forEach((chk) => {
          chk.itemSelections.selected.forEach((item) => {
            if (item.seatNames && item.seatNames.length) {
              item.seatNames.forEach((seatName) => seatNames.set(seatName, seatName));
            }
          });
        });
        return [...seatNames.values()] as string[];
      },
      can: {
        get createSeatChecks() {
          return Boolean(store.seats.selectedSeatNames.length);
        },
        get assignSeatPosition() {
          return hasSomeCheckItemSelections(store.checks);
        },
      },
      assignSeatPositions: (seatNames: string[]) => {
        const nextChecks = store.checks.map((chk) => {
          chk.itemSelections.selected.forEach((item) => {
            item.update({ seatNames });
          });
          return chk;
        });
        checksSet(nextChecks);
      },
      getCheckFromSeat: (seatName: string) => store.checks.find((chk) => chk.seatName === seatName),
      createSeatChecks: async (seatNames: string[]) => {
        // build checks
        for (let i = 0; i < seatNames.length; i++) {
          const seatName = seatNames[i];
          const existingCheck = store.seats.getCheckFromSeat(seatName);
          if (!existingCheck) {
            await store.onAddNewCheck(seatName);
          }
        }
        // sort by label each time
        let nextChecks = store.checks.sort(sortByLabel);
        // de-select any check item(s) that do not have existing seat
        nextChecks = nextChecks.map((chk) => {
          chk.itemSelections.selected.forEach((item) => {
            if (!item.seatNames || !item.seatNames.length) item.isSelected = false;
          });
          return chk;
        });

        // !!! make sure to deselect each item after it is split or moved ( not a fan of while loops )
        while (hasSomeCheckItemSelections(nextChecks)) {
          const checkWithItemSelections = nextChecks.find((chk) => chk.itemSelections.selected.length);
          if (checkWithItemSelections) {
            const nextCheckItem = checkWithItemSelections.itemSelections.selected[0];
            const from: CheckSplitUI[] = [];
            const to: CheckSplitUI[] = [];
            from.push(new CheckSplitUI({ ...checkWithItemSelections, items: [nextCheckItem] }, { maintainItemUid: true }));
            nextCheckItem.isSelected = false;
            nextCheckItem.seatNames.forEach((seatName) => {
              const seatCheck = store.seats.getCheckFromSeat(seatName);
              if (seatCheck) {
                to.push(new CheckSplitUI(seatCheck, { maintainItemUid: true }));
              }
            });
            nextChecks = store.moveOrSplit({ from, to });
          } else break;
        }
        checksSet(nextChecks);
        hasChangesSet(true);
      },
    },
    grid: {
      get style() {
        const columns = store.checks.length + 1; //add 1 for the '+' button
        const rems = {
          gridGap: 4.8,
          itemWidth: 54,
        };
        const rows = 1;
        const style: CSSProperties = {
          display: 'grid',
          height: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          gap: getRemCssValue(rems.gridGap),
          gridTemplateColumns: `repeat(${columns - 1}, ${getRemCssValue(rems.itemWidth)}) auto`, // account for '+' and 'split by seat' buttons
          gridTemplateRows: `repeat(${rows}, 1fr)`,
          paddingInline: `2rem`,
          paddingBottom: `2rem`,
          justifyContent: 'start',
        };
        return style;
      }
    },
    onBack: () => {
      navigate(crumbs.pos.check.main.interpolate({
        checkId: currentCheck.query.data?.id,
        itemSelect: route.params.active.itemSelect,
      }));
    },
    onSplitBySeat: async () => {
      const startingCheck = store.checks[0];
      const startingChecks = store.checks.slice();
      const seatCheckMap = new Map<string | -1, CheckSplitUI | undefined>();
      // first, put each seat in its own place in the map
      startingCheck.items.forEach((itm) => {
        if (!itm.can.edit) return;
        if (itm.seatNames.length === 0) seatCheckMap.set(-1, undefined);
        else itm.seatNames.forEach((seatName) => seatCheckMap.set(seatName, undefined));
      });
      // asign a check to each seat in the map
      let checkI = 0;
      let newId = -1;
      store.checks.forEach((chk) => {
        if (chk.id <= newId) newId = chk.id - 1;
      });
      for (const [key] of seatCheckMap) {
        if (checkI < startingChecks.length) {
          seatCheckMap.set(key, startingChecks[checkI]);
          checkI++;
        } else {
          const response = await allocateNextCheckNumber.mutateAsync();
          let label = startingCheck?.number || '';
          label = `${label} - ${response.checkNumber}`;
          const newCheck = new CheckSplitUI(new Check({ id: newId, label, number: response.checkNumber }));
          newId--;
          store.checks.push(newCheck);
          seatCheckMap.set(key, newCheck);
        }
      }
      // split items
      const itemsToSplit = startingCheck.items;
      let nextChecks: CheckSplitUI[] = [];
      itemsToSplit.forEach((its) => {
        if (!its.can.edit) return;
        // setup the from/to checks for this item
        const from: CheckSplitUI[] = [];
        from.push(new CheckSplitUI({ ...startingCheck, items: [its] }, { maintainItemUid: true }));
        const to: CheckSplitUI[] = [];
        if (its.seatNames.length === 0) to.push(new CheckSplitUI(seatCheckMap.get(-1) as CheckSplitUI, { maintainItemUid: true }));
        else its.seatNames.forEach((seatName) => to.push(new CheckSplitUI(seatCheckMap.get(seatName) as CheckSplitUI, { maintainItemUid: true })));
        // special case... if the item has a single target check, and that check is the starting check (meaning the item is already on that check),
        // then we don't want to do any splitting
        if (to.length === 1 && to[0].id === startingCheck.id) return;
        // split the item on to checks
        nextChecks = store.moveOrSplit({ from, to });
        // now that checks have been updated, update the map to hold the (new referential) checks
        nextChecks.forEach((chk) => seatCheckMap.forEach((chkInMap, key) => {
          if (chk.id === chkInMap?.id) seatCheckMap.set(key, chk);
        }));
      });
      // save changes
      if (nextChecks.length > 0) {
        hasChangesSet(true);
        checksSet(nextChecks);
      }
    },
  };

  // separating from mob-x store to avoid complex memo-tising of options
  const initialize = async () => {
    hasChangesSet(false);
    // add the current check as the first item to the store
    // clone the first check to avoid augmenting the original new check
    const baseCheck = new CheckSplitUI(currentCheck.query.data as Check);
    // transfer selected states from current check
    baseCheck.items = baseCheck.items.map((item) => {
      item.isSelected = options.currentCheckSelectedCheckItemIds.includes(item.id);
      return item;
    });
    // create empty check to start
    const emptyCheck = await createEmptyCheck(baseCheck);
    // done
    checksSet([baseCheck, emptyCheck]);
  };

  useEffect(() => {
    if (currentCheck.query.data && !store.checks.length) initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCheck.query.data]);

  return { store, initialize };
};

export { CheckItemSplitUI, CheckSplitUI, useCheckSplit };
